import cloneDeep from 'lodash/cloneDeep';
import {
	createActionsHook,
	createStateHook,
	createStore,
	defaultRegistry,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type Store as StoreType,
} from 'react-sweet-state';

import { PARENT_FIELD_ID } from '../../common/constants';
import type { DraftWorkItem } from '../../common/types';

import type { DraftWorkItemActions, DraftWorkItemsState, MessageState } from './types';

/**
 * This sweet state is a global singleton so we can track inline edits between conversations.
 * We store a map of message states, each with a draft work items map and top level invocation ids.
 */
const initialMessageState: MessageState = {
	isInitialised: false,
	hasAppliedDraftEdits: false,
	draftWorkItemsMap: {},
	topLevelInvocationIds: [],
};

const initialState: DraftWorkItemsState = {
	messages: {},
};

export const getMessageState = (state: DraftWorkItemsState, messageId: string): MessageState =>
	state.messages[messageId] ?? initialMessageState;

/**
 * Removes a work item from the map and handles parent/child relationships.
 */
const removeFromMap = (
	draftItemsMap: Record<string, DraftWorkItem>,
	invocationId: string,
): { newMap: Record<string, DraftWorkItem>; promotedInvocationIds: string[] } => {
	if (!draftItemsMap[invocationId]) {
		return { newMap: draftItemsMap, promotedInvocationIds: [] };
	}

	const newMap = cloneDeep(draftItemsMap);
	const childInvocationIds = newMap[invocationId].childInvocationIds || [];
	const parent = newMap[invocationId]?.fields[PARENT_FIELD_ID];

	delete newMap[invocationId];

	const parentInvocationId = Object.values(newMap).find(
		(item) => item.summary === parent || item?.createdIssueKey === parent,
	)?.invocationId;

	if (parentInvocationId) {
		newMap[parentInvocationId].childInvocationIds =
			newMap[parentInvocationId].childInvocationIds?.filter(
				(childInvocationId) => childInvocationId !== invocationId,
			) || [];
	}

	const promotedInvocationIds: string[] = [];
	childInvocationIds.forEach((childInvocationId) => {
		if (newMap[childInvocationId].status !== 'finished') {
			delete newMap[childInvocationId];
		} else {
			promotedInvocationIds.push(childInvocationId);
			newMap[childInvocationId].fields[PARENT_FIELD_ID] = undefined;
		}
	});

	return { newMap, promotedInvocationIds };
};

const actions: DraftWorkItemActions = {
	initDraftWorkItems:
		(
			messageId: string,
			invocationIdToWorkItemMap: Record<string, DraftWorkItem>,
			topLevelInvocationIds: string[],
		) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			// Only initialise once — subsequent calls are no-ops once the message is initialised.
			if (getMessageState(getState(), messageId).isInitialised) {
				return;
			}

			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						isInitialised: true,
						hasAppliedDraftEdits: false,
						draftWorkItemsMap: invocationIdToWorkItemMap,
						topLevelInvocationIds,
					},
				},
			});
		},

	setDraftWorkItems:
		(
			messageId: string,
			invocationIdToWorkItemMap: Record<string, DraftWorkItem>,
			topLevelInvocationIds: string[],
		) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						isInitialised: true,
						hasAppliedDraftEdits: true,
						draftWorkItemsMap: invocationIdToWorkItemMap,
						topLevelInvocationIds,
					},
				},
			});
		},

	onFieldChange:
		(messageId: string, invocationId: string, fieldId: string, newValue: unknown) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			const item = draftWorkItemsMap[invocationId];
			if (!item) {
				return;
			}

			const updatedFields = { ...item.fields, [fieldId]: newValue ?? undefined };
			const nextItems = { ...draftWorkItemsMap };

			// Handle summary field changes — need to update parent field in children too
			if (fieldId === 'summary' && typeof newValue === 'string') {
				nextItems[invocationId] = {
					...item,
					summary: newValue,
					fields: updatedFields,
				};
				item.childInvocationIds?.forEach((childInvocationId) => {
					const child = nextItems[childInvocationId];
					if (child) {
						nextItems[childInvocationId] = {
							...child,
							fields: { ...child.fields, [PARENT_FIELD_ID]: newValue },
						};
					}
				});
			} else {
				nextItems[invocationId] = { ...item, fields: updatedFields };
			}

			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						...getMessageState(getState(), messageId),
						draftWorkItemsMap: nextItems,
					},
				},
			});
		},

	onBulkFieldChange:
		(messageId: string, invocationId: string, newFields: Record<string, unknown>) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			const item = draftWorkItemsMap[invocationId];
			if (!item) {
				return;
			}

			const nextItems = { ...draftWorkItemsMap };
			const newSummary = typeof newFields.summary === 'string' ? newFields.summary : item.summary;
			const issuetype = newFields.issuetype;
			const newIssueTypeId =
				typeof issuetype === 'object' &&
				issuetype !== null &&
				'issueTypeId' in issuetype &&
				typeof issuetype.issueTypeId === 'string'
					? issuetype.issueTypeId
					: item.issueTypeId;

			nextItems[invocationId] = {
				...item,
				summary: newSummary,
				issueTypeId: newIssueTypeId,
				fields: newFields,
			};

			// If summary changed, update parent field in all children
			if (newSummary !== item.summary) {
				item.childInvocationIds?.forEach((childInvocationId) => {
					const child = nextItems[childInvocationId];
					if (child) {
						nextItems[childInvocationId] = {
							...child,
							fields: { ...child.fields, [PARENT_FIELD_ID]: newSummary },
						};
					}
				});
			}

			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						...getMessageState(getState(), messageId),
						// Prevents stale BE fetch from overwriting new GIC edits - defensive correctness as it is very likely
						// the BE fetch would have already been applied by the time a user opens GIC and saves their changes.
						hasAppliedDraftEdits: true,
						draftWorkItemsMap: nextItems,
					},
				},
			});
		},

	removeDraftWorkItem:
		(messageId: string, invocationId: string) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): boolean => {
			const { draftWorkItemsMap, topLevelInvocationIds } = getMessageState(getState(), messageId);
			if (!draftWorkItemsMap[invocationId]) {
				return false;
			}
			const { newMap, promotedInvocationIds } = removeFromMap(draftWorkItemsMap, invocationId);
			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						...getMessageState(getState(), messageId),
						draftWorkItemsMap: newMap,
						topLevelInvocationIds: [...topLevelInvocationIds, ...promotedInvocationIds],
					},
				},
			});
			return true;
		},

	onDraftWorkItemsCreated:
		(messageId: string, invocationIdToCreatedIssueKey: Record<string, string>) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			const nextItems: Record<string, DraftWorkItem> = { ...draftWorkItemsMap };

			Object.entries(invocationIdToCreatedIssueKey).forEach(([invocationId, issueKey]) => {
				const item = nextItems[invocationId];
				if (!item) {
					return;
				}
				nextItems[invocationId] = {
					...item,
					createdIssueKey: issueKey,
					status: 'finished',
				};
				item.childInvocationIds?.forEach((childInvocationId) => {
					const child = nextItems[childInvocationId];
					if (child) {
						nextItems[childInvocationId] = {
							...child,
							fields: { ...child.fields, [PARENT_FIELD_ID]: issueKey },
						};
					}
				});
			});

			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						...getMessageState(getState(), messageId),
						draftWorkItemsMap: nextItems,
					},
				},
			});
		},

	onDismissAllDraftWorkItems:
		(messageId: string) =>
		({
			setState,
			getState,
		}: {
			setState: (s: Partial<DraftWorkItemsState>) => void;
			getState: () => DraftWorkItemsState;
		}): void => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			const newMap = { ...draftWorkItemsMap };
			Object.values(newMap).forEach((item) => {
				if (item.status !== 'finished') {
					delete newMap[item.invocationId];
				}
			});
			setState({
				messages: {
					...getState().messages,
					[messageId]: {
						...getMessageState(getState(), messageId),
						draftWorkItemsMap: newMap,
						topLevelInvocationIds: Object.keys(newMap),
					},
				},
			});
		},

	getDraftWorkItemsWithoutHierarchy:
		(messageId: string) =>
		({ getState }: { getState: () => DraftWorkItemsState }): DraftWorkItem[] => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			return Object.values(draftWorkItemsMap);
		},

	getAllActiveInvocationIds:
		(messageId: string) =>
		({ getState }: { getState: () => DraftWorkItemsState }): string[] => {
			const { draftWorkItemsMap } = getMessageState(getState(), messageId);
			return Object.values(draftWorkItemsMap)
				.filter((item) => item.status !== 'cancelled' && !item.createdIssueKey)
				.map((item) => item.invocationId);
		},
};

const draftWorkItemsStore: StoreType<DraftWorkItemsState, DraftWorkItemActions> = createStore({
	initialState: initialState,
	actions: actions,
});

export const Store: StoreType<DraftWorkItemsState, DraftWorkItemActions> = draftWorkItemsStore;

const useDraftWorkItemsStateHook: HookStateFunction<DraftWorkItemsState, void> =
	createStateHook(Store);
export const useDraftWorkItemsState: typeof useDraftWorkItemsStateHook = useDraftWorkItemsStateHook;

const useDraftWorkItemsActionsHook: HookActionsFunction<
	BoundActions<DraftWorkItemsState, DraftWorkItemActions>
> = createActionsHook(Store);
export const useDraftWorkItemsActions: typeof useDraftWorkItemsActionsHook =
	useDraftWorkItemsActionsHook;

/**
 * Resets the draft work items store to its initial state.
 * Used for testing only.
 */
export const resetDraftWorkItemsStore = (): void => {
	defaultRegistry.stores.clear();
};
